import type { Call } from '@bcio-voice/contracts';
import type { CommandView } from './commands.js';

/** The local SIP leg of this device. Never implies anything about the other party. */
export type LegState = 'none' | 'offered' | 'accepting' | 'established' | 'terminating' | 'terminated';

export const LEG_TRANSITIONS: Readonly<Record<LegState, readonly LegState[]>> = Object.freeze({
  none: ['accepting', 'terminated'],
  offered: ['accepting', 'terminating', 'terminated'],
  accepting: ['established', 'terminating', 'terminated'],
  established: ['terminating', 'terminated'],
  terminating: ['terminated'],
  terminated: [],
});

export const canTransitionLeg = (from: LegState, to: LegState): boolean => LEG_TRANSITIONS[from].includes(to);
export const legLive = (leg: LegState): boolean => leg !== 'terminated';

/**
 * UI phase (UX spec 7.2). Server call state wins over local guesses: `active`, `held`, `ringing`,
 * `ended` and `failed` are only ever derived from the server `Call`, never from a command receipt
 * or from the local SIP leg alone.
 */
export type CallPhase =
  | 'requesting'
  | 'unconfirmed'
  | 'connecting'
  | 'ringing'
  | 'offered'
  | 'answering'
  | 'active'
  | 'held'
  | 'ending'
  | 'ended'
  | 'failed';

export interface PhaseInput {
  readonly origin: 'outbound' | 'inbound';
  readonly leg: LegState;
  readonly server: Pick<Call, 'state'> | null;
  readonly create: Pick<CommandView, 'phase'> | null;
  readonly cancelRequested: boolean;
  readonly localFailure: boolean;
}

export const terminalCall = (call: Pick<Call, 'state'> | null | undefined): boolean => call?.state === 'ended' || call?.state === 'failed';

export function derivePhase(input: PhaseInput): CallPhase {
  const state = input.server?.state;
  if (state === 'ended') return 'ended';
  if (state === 'failed') return 'failed';
  if (input.cancelRequested && input.leg === 'terminated') return 'ended';
  if (input.localFailure) return 'failed';
  if (input.cancelRequested || input.leg === 'terminating' || input.leg === 'terminated') return 'ending';
  if (input.origin === 'outbound') {
    const create = input.create?.phase;
    if (create === undefined || create === 'sending') return 'requesting';
    if (create === 'unconfirmed') return 'unconfirmed';
    if (input.leg !== 'established') return 'connecting';
    if (state === 'active') return 'active';
    if (state === 'held') return 'held';
    if (state === 'ringing') return 'ringing';
    return 'connecting';
  }
  if (input.leg === 'offered') return 'offered';
  if (input.leg === 'accepting') return 'answering';
  if (state === 'active') return 'active';
  if (state === 'held') return 'held';
  return 'answering';
}
